/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import MuiCheckbox, { CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import React from 'react';

import { Spacing } from 'src/common/ui/base/Spacing';

/**
 * Checkbox component with optional label
 */
export const Checkbox = ({
  checked,
  className,
  'data-testid': dataTestid,
  disabled,
  id,
  label,
  name,
  onChange,
  required = false,
  value,
}: {
  checked?: boolean;
  className?: string;
  'data-testid'?: string;
  disabled?: boolean;
  id?: string;
  label?: React.ReactNode;
  name?: string;
  onChange?: MuiCheckboxProps['onChange'];
  required?: boolean;
  value?: unknown;
}) => {
  const checkbox = (
    <MuiCheckbox
      checked={checked}
      className={className}
      data-testid={dataTestid || 'nds-component-checkbox'}
      disabled={disabled}
      id={id}
      name={name}
      onChange={onChange}
      required={required}
      size="small"
      value={value}
      css={css`
        padding: ${Spacing.Unit1}px;
      `}
    />
  );

  if (label) {
    return (
      <FormControlLabel
        control={checkbox}
        label={label}
        disabled={disabled}
        css={css`
          margin: 0;

          .MuiFormControlLabel-label {
            position: relative;
            top: 1px; // Visually center the label with the checkbox
          }
        `}
      />
    );
  }

  return checkbox;
};

/**
 * CheckboxGroup component for grouping related checkboxes.
 *
 * @example
 * <CheckboxGroup>
 *   <Checkbox label="Option 1" checked={checked1} onChange={handleChange1} />
 *   <Checkbox label="Option 2" checked={checked2} onChange={handleChange2} />
 * </CheckboxGroup>
 */
export const CheckboxGroup = ({
  children,
  className,
  'data-testid': dataTestid,
  direction = 'column',
}: {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  direction?: 'row' | 'column';
}) => (
  <div
    className={className}
    data-testid={dataTestid || 'nds-component-checkbox-group'}
    css={css`
      display: flex;
      flex-direction: ${direction};
      gap: ${Spacing.Unit2}px;
    `}
  >
    {children}
  </div>
);
